import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    approveObjectifRequest,
    approveObjectifModificationRequest,
    createObjectifRequest,
    fetchManagerPendingObjectifModificationsRequest,
    fetchProgressObjectifModificationRequestsRequest,
    fetchObjectifsNRequest,
    rejectObjectifModificationRequest,
    rejectObjectifRequest,
    updateObjectifRequest,
} from "../../../../redux/actions/performanceActions";
import { asArray, StepState } from "../../Collaborateur/performanceUi";
import ObjectifsAIAssistanceModal, { buildObjectifsAiStats } from "../../Collaborateur/steps/ObjectifsAIAssistanceModal";
import Swal from "sweetalert2";
import { Pencil } from "lucide-react";
import "./ObjectifsNManagerStep.css";

const emptyObjectif = {
    titre: "",
    description: "",
    indicateur: "",
    performanceCible: "",
    poids: "",
    typeObjectif: "QUANTITATIF",
    priorite: "",
    typeIndicateur: "",
    surperformance: "",
    tolerance: "",
    dateFin: "",
};

function Field({ label, children, required }) {
    return (
        <div className="on-field">
            <label className="on-field__label">
                {label}
                {required && <span className="on-field__required"> *</span>}
            </label>
            {children}
        </div>
    );
}

function StatBadge({ value, label, color = "blue" }) {
    return (
        <div className={`on-stat on-stat--${color}`}>
            <span className="on-stat__value">{value}</span>
            <span className="on-stat__label">{label}</span>
        </div>
    );
}

const isRejectedStatus = (status) => ["REJECTED", "REFUSED"].includes(status);
const isWeightCounted = (objectif) => ["APPROVED", "PENDING_APPROVAL"].includes(objectif.approvalStatus);
const isPendingApproval = (objectif) => objectif.approvalStatus === "PENDING_APPROVAL";

const decisionLabel = (status) => {
    if (status === "APPROVED") return "Approuvé";
    if (status === "PENDING_APPROVAL") return "En attente d’approbation";
    if (status === "REJECTED") return "Rejeté";
    if (status === "REFUSED") return "Refusé";
    if (status === "EN_COURS") return "En cours";
    if (status === "TERMINE") return "Terminé";
    if (status === "COMPLETED") return "Terminé";
    if (status === "NOT_STARTED") return "Non démarré";
    if (status === "IN_PROGRESS") return "En cours";
    if (status === "PENDING_VALIDATION") return "En attente de validation";
    return status || "Objectif";
};
const modificationFields = [
    ["titre", "Titre", "oldTitre"],
    ["indicateur", "Indicateur", "oldIndicateur"],
    ["typeIndicateur", "Type indicateur", "oldTypeIndicateur"],
];
const revisionFields = [
    ["performanceCible", "Performance cible", "oldPerformanceCible"],
    ["poids", "Poids", "oldPoids"],
    ["dateFin", "Date echeance", "oldDateFin"],
    ["surperformance", "Surperformance", "oldSurperformance"],
    ["tolerance", "Tolerance", "oldTolerance"],
];
const requestStatusLabel = (status) => {
    if (status === "SOUMISE") return "En attente";
    if (status === "APPROUVEE") return "Approuvée";
    if (status === "REFUSEE") return "Refusée";
    if (status === "ANNULEE") return "Annulée";
    return status || "Demande";
};
const showRequiredPopup = (text) => {
    Swal.fire({
        icon: "warning",
        title: "Champ obligatoire",
        text,
        confirmButtonText: "Compris",
    });
};
const objectifToForm = (objectif) => ({
    titre: objectif.titre || "",
    description: objectif.description || "",
    indicateur: objectif.indicateur || "",
    performanceCible: objectif.performanceCible ?? "",
    poids: objectif.poids ?? "",
    typeObjectif: objectif.typeObjectif || "QUANTITATIF",
    priorite: objectif.priorite ?? "",
    typeIndicateur: objectif.typeIndicateur || "",
    surperformance: objectif.surperformance ?? "",
    tolerance: objectif.tolerance ?? "",
    dateFin: objectif.dateFin || "",
});

export default function ObjectifsNManagerStep({ progressId }) {
    const dispatch = useDispatch();
    const slice = useSelector((state) => state.performance.objectifsN);
    const data = slice.dataByProgressId[String(progressId)];
    const loading = slice.loadingByProgressId[String(progressId)];
    const error = slice.errorByProgressId[String(progressId)];
    const objectifs = asArray(data);
    const stepper = useSelector((state) => state.performance.progress.stepperByProgressId[String(progressId)]);
    const managerPendingRequests = useSelector((state) => state.performance.modificationsObjectifs.managerPending);
    const modificationHistory = useSelector((state) => state.performance.modificationsObjectifs.byObjectifId);
    const pendingRequests = asArray(managerPendingRequests);
    const [comments, setComments] = useState({});
    const [requestComments, setRequestComments] = useState({});
    const [form, setForm] = useState(emptyObjectif);
    const [editingObjectifId, setEditingObjectifId] = useState(null);
    const [editForm, setEditForm] = useState(emptyObjectif);
    const [formOpen, setFormOpen] = useState(true);
    const [aiModalOpen, setAiModalOpen] = useState(false);

    useEffect(() => {
        dispatch(fetchObjectifsNRequest(progressId));
        dispatch(fetchProgressObjectifModificationRequestsRequest(progressId));
        dispatch(fetchManagerPendingObjectifModificationsRequest());
    }, [dispatch, progressId]);

    const updateForm = (patch) => setForm((current) => ({ ...current, ...patch }));
    const updateEditForm = (patch) => setEditForm((current) => ({ ...current, ...patch }));

    const create = (event) => {
        event.preventDefault();
        if (form.poids === "" || form.poids == null) {
            showRequiredPopup("Poids nul, veuillez le saisir.");
            return;
        }
        dispatch(createObjectifRequest({
            ...form,
            evaluationProgressId: Number(progressId),
        }));
        setForm(emptyObjectif);
    };
    const startEdit = (objectif) => {
        setEditingObjectifId(objectif.id);
        setEditForm(objectifToForm(objectif));
    };
    const cancelEdit = () => {
        setEditingObjectifId(null);
        setEditForm(emptyObjectif);
    };
    const saveEdit = (event, objectif) => {
        event.preventDefault();
        if (editForm.poids === "" || editForm.poids == null) {
            showRequiredPopup("Poids nul, veuillez le saisir.");
            return;
        }
        dispatch(updateObjectifRequest(objectif.id, {
            ...editForm,
            evaluationProgressId: Number(progressId),
            collaborateurId: objectif.collaborateurId,
        }, progressId));
        cancelEdit();
    };

    const payload = (objectif) => ({
        evaluationProgressId: Number(progressId),
        commentaire: comments[objectif.id] || "",
    });

    const totalPoids = objectifs
        .filter(isWeightCounted)
        .reduce((sum, objectif) => sum + (Number(objectif.poids) || 0), 0);
    const aiStats = useMemo(() => buildObjectifsAiStats(objectifs, stepper), [objectifs, stepper]);
    const showAiComingSoon = () => {
        Swal.fire({
            icon: "info",
            title: "Assistance IA",
            text: "La configuration est prête. Le branchement de génération IA peut être ajouté côté backend.",
            confirmButtonText: "Compris",
        });
    };
    const objectifIds = new Set(objectifs.map((objectif) => objectif.id));
    const requestsForProgress = pendingRequests.filter((request) => objectifIds.has(request.objectifId));
    const decideRequest = (request, approved) => {
        const payload = { commentaire: requestComments[request.id] || "" };
        if (approved) dispatch(approveObjectifModificationRequest(request.id, payload, progressId));
        else dispatch(rejectObjectifModificationRequest(request.id, payload, progressId));
    };
    const fieldsForRequest = (request) => request.requestType === "REVISION" ? revisionFields : modificationFields;
    const requestObjectifTitle = (request) =>
        request.objectifTitre || request.oldTitre || request.titre || `Objectif #${request.objectifId}`;

    const renderRequestHistory = (objectif) => {
        const history = asArray(modificationHistory[String(objectif.id)]).filter((request) => request.requestType !== "REVISION");
        if (!history.length) return null;
        return (
            <div className="on-request-history">
                <div className="on-request-history__title">Historique des demandes</div>
                <div className="on-request-history__list">
                    {history.map((request) => (
                        <div className="on-request-history__item" key={request.id}>
                            <div className="on-request-history__head">
                                <span className="on-request-history__type">{request.requestType || "MODIFICATION"}</span>
                                <span className={`on-status-badge on-status-badge--${String(request.status || "").toLowerCase()}`}>
                                    {requestStatusLabel(request.status)}
                                </span>
                            </div>
                            <div className="on-request-history__meta">
                                {request.requestedAt ? new Date(request.requestedAt).toLocaleString("fr-FR") : "-"}
                                {request.decidedAt ? ` - Décision: ${new Date(request.decidedAt).toLocaleString("fr-FR")}` : ""}
                            </div>
                            {request.justification && <p className="on-request-history__text">Justification: {request.justification}</p>}
                            {request.decisionComment && <p className="on-request-history__text">Commentaire manager: {request.decisionComment}</p>}
                            <div className="on-request-history__diffs">
                                {fieldsForRequest(request).map(([field, label, oldField]) => request[field] != null && (
                                    <div className="on-request-history__diff" key={field}>
                                        <span>{label}</span>
                                        <strong>{request[oldField] ?? "-"} -> {request[field]}</strong>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <StepState loading={loading} error={error} empty={false}>
            <div className="on-page on-manager-page">
                <div className="on-header">
                    <div className="on-header__left">
                        <h2 className="on-header__title">Objectifs N</h2>
                        <p className="on-header__sub">Ajoutez, validez et gérez les objectifs du collaborateur</p>
                    </div>
                    <div className="on-header__stats">
                        <StatBadge value={objectifs.length} label="Objectifs" color="blue" />
                        <StatBadge value={`${totalPoids}%`} label="Poids total" color={totalPoids === 100 ? "green" : totalPoids > 100 ? "red" : "orange"} />
                    </div>
                </div>

                <div className="on-card">
                    <button
                        type="button"
                        className="on-card__toggle"
                        onClick={() => setFormOpen((value) => !value)}
                        aria-expanded={formOpen}
                    >
                        <span className="on-card__toggle-icon">{formOpen ? "-" : "+"}</span>
                        <span className="on-card__toggle-label">Nouvel objectif N (Manager)</span>
                    </button>

                    {formOpen && (
                        <form className="on-form" onSubmit={create}>
                            <div className="on-form__section-label">Informations generales</div>
                            <div className="on-form__row on-form__row--3">
                                <Field label="Titre" required>
                                    <input className="on-input" required value={form.titre} onChange={(event) => updateForm({ titre: event.target.value })} placeholder="Intitule de l'objectif" />
                                </Field>
                                <Field label="Priorité">
                                    <input className="on-input" type="number" min="1" value={form.priorite} onChange={(event) => updateForm({ priorite: event.target.value })} placeholder="1" />
                                </Field>
                                <Field label="Date d'échéance">
                                    <input className="on-input" type="date" value={form.dateFin} onChange={(event) => updateForm({ dateFin: event.target.value })} />
                                </Field>
                            </div>

                            <div className="on-form__section-label">Indicateurs & mesure</div>
                            <div className="on-form__row on-form__row--4">
                                <Field label="Type d'indicateur">
                                    <select className="on-select" value={form.typeIndicateur} onChange={(event) => updateForm({ typeIndicateur: event.target.value })}>
                                        <option value="">Sélectionner</option>
                                        <option value="Pourcentage">Pourcentage</option>
                                        <option value="Nombre">Nombre</option>
                                        <option value="Taux">Taux</option>
                                    </select>
                                </Field>
                                <Field label="Indicateur">
                                    <input className="on-input" value={form.indicateur} onChange={(event) => updateForm({ indicateur: event.target.value })} placeholder="KPI ou metrique" />
                                </Field>
                                <Field label="Performance cible">
                                    <input className="on-input" type="number" step="0.01" value={form.performanceCible} onChange={(event) => updateForm({ performanceCible: event.target.value })} placeholder="0.00" />
                                </Field>
                                <Field label="Poids (%)">
                                    <input className="on-input" type="number" value={form.poids} onChange={(event) => updateForm({ poids: event.target.value })} placeholder="0" />
                                </Field>
                            </div>

                            <div className="on-form__row on-form__row--2">
                                <Field label="Surperformance">
                                    <input className="on-input" type="number" step="0.01" value={form.surperformance} onChange={(event) => updateForm({ surperformance: event.target.value })} placeholder="0.00" />
                                </Field>
                                <Field label="Tolérance">
                                    <input className="on-input" type="number" step="0.01" value={form.tolerance} onChange={(event) => updateForm({ tolerance: event.target.value })} placeholder="0.00" />
                                </Field>
                            </div>

                            <div className="on-form__section-label">Type d'objectif</div>
                            <div className="on-radio-group">
                                <label className={`on-radio-option ${form.typeObjectif === "QUANTITATIF" ? "on-radio-option--active" : ""}`}>
                                    <input type="radio" name="typeObjectif" value="QUANTITATIF" checked={form.typeObjectif === "QUANTITATIF"} onChange={(event) => updateForm({ typeObjectif: event.target.value })} />
                                    <span className="on-radio-option__dot" />
                                    <span>Quantitatif</span>
                                </label>
                                <label className={`on-radio-option ${form.typeObjectif === "QUALITATIF" ? "on-radio-option--active" : ""}`}>
                                    <input type="radio" name="typeObjectif" value="QUALITATIF" checked={form.typeObjectif === "QUALITATIF"} onChange={(event) => updateForm({ typeObjectif: event.target.value })} />
                                    <span className="on-radio-option__dot" />
                                    <span>Qualitatif</span>
                                </label>
                            </div>

                            <Field label="Description">
                                <textarea className="on-textarea" rows={3} value={form.description} onChange={(event) => updateForm({ description: event.target.value })} placeholder="Decrivez l'objectif en detail..." />
                            </Field>

                            <div className="on-form__footer">
                                <button className="on-btn on-btn--primary" type="submit">
                                    <span>+</span> Ajouter l'objectif (Auto-valide)
                                </button>
                                <button className="on-btn on-btn--ai" type="button" onClick={() => setAiModalOpen(true)}>
                                    Assistance IA
                                </button>
                            </div>
                        </form>
                    )}
                </div>

                <ObjectifsAIAssistanceModal
                    open={aiModalOpen}
                    onClose={() => setAiModalOpen(false)}
                    stats={aiStats}
                    onGenerate={showAiComingSoon}
                />

                <div className="on-list-header">
                    <h3 className="on-list-title">Objectifs enregistrés ({objectifs.length})</h3>
                </div>

                {requestsForProgress.length > 0 && (
                    <div className="on-card on-pending-requests-card">
                        <h3 className="on-list-title">Demandes collaborateur en attente ({requestsForProgress.length})</h3>
                        <div className="on-stack">
                            {requestsForProgress.map((request) => (
                                <article className="on-item" key={request.id}>
                                    <div className="on-item__head">
                                        <div className="on-item__head-left">
                                            <span className="on-status-badge on-status-badge--pending_approval">{request.requestType || "MODIFICATION"}</span>
                                            <h3 className="on-item__title">{requestObjectifTitle(request)}</h3>
                                        </div>
                                        <span className="on-status-badge on-status-badge--pending_approval">{requestStatusLabel(request.status)}</span>
                                    </div>
                                    <p className="on-item__desc">{request.justification}</p>
                                    <div className="on-facts">
                                        {fieldsForRequest(request).map(([field, label, oldField]) => request[field] != null && (
                                            <div className="on-fact" key={field}>
                                                <span className="on-fact__key">{label}</span>
                                                <span className="on-fact__val">Ancien: {request[oldField] ?? "-"}</span>
                                                <span className="on-fact__val on-fact__val--accent">Nouveau: {request[field]}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="on-item__footer on-manager-decision">
                                        <input className="on-input on-input--sm" placeholder="Commentaire manager" value={requestComments[request.id] || ""} onChange={(event) => setRequestComments((current) => ({ ...current, [request.id]: event.target.value }))} />
                                        <button className="on-btn on-btn--primary" type="button" onClick={() => decideRequest(request, true)}>Approuver</button>
                                        <button className="on-btn on-btn--outline on-btn--danger" type="button" onClick={() => decideRequest(request, false)}>Refuser</button>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                )}

                {objectifs.length === 0 ? (
                    <div className="on-empty">
                        <div className="on-empty__icon">+</div>
                        <p>Aucun objectif N pour le moment.</p>
                        <span>Ajoutez le premier objectif via le formulaire ci-dessus.</span>
                    </div>
                ) : (
                    <div className="on-stack">
                        {objectifs.map((objectif) => {
                            const status = objectif.approvalStatus || objectif.statusObjectif || "OBJECTIF";
                            const canDecide = isPendingApproval(objectif);
                            const isEditing = editingObjectifId === objectif.id;
                            return (
                                <article className="on-item" key={objectif.id}>
                                    <div className="on-item__head">
                                        <div className="on-item__head-left">
                                            <button
                                                className="on-icon-btn on-objectif-edit-btn"
                                                type="button"
                                                title="Modifier l'objectif"
                                                aria-label="Modifier l'objectif"
                                                onClick={() => startEdit(objectif)}
                                            >
                                                <Pencil size={15} />
                                            </button>
                                            <span className="on-item__priority">#{objectif.priorite ?? "-"}</span>
                                            <h3 className="on-item__title">{objectif.titre}</h3>
                                        </div>
                                        <div className="on-item__head-right">
                                            {objectif.poids != null && <span className="on-poids-badge">{objectif.poids}%</span>}
                                            <span className={`on-status-badge on-status-badge--${String(status).toLowerCase()}`}>
                                                {decisionLabel(status)}
                                            </span>
                                        </div>
                                    </div>

                                    {isEditing && (
                                        <form className="on-form on-edit-form" onSubmit={(event) => saveEdit(event, objectif)}>
                                            <div className="on-form__section-label">Modifier l'objectif N</div>
                                            <div className="on-form__row on-form__row--3">
                                                <Field label="Titre" required>
                                                    <input className="on-input" required value={editForm.titre} onChange={(event) => updateEditForm({ titre: event.target.value })} />
                                                </Field>
                                                <Field label="Priorite">
                                                    <input className="on-input" type="number" min="1" value={editForm.priorite} onChange={(event) => updateEditForm({ priorite: event.target.value })} />
                                                </Field>
                                                <Field label="Date d'echeance">
                                                    <input className="on-input" type="date" value={editForm.dateFin} onChange={(event) => updateEditForm({ dateFin: event.target.value })} />
                                                </Field>
                                            </div>
                                            <div className="on-form__row on-form__row--4">
                                                <Field label="Type d'indicateur">
                                                    <select className="on-select" value={editForm.typeIndicateur} onChange={(event) => updateEditForm({ typeIndicateur: event.target.value })}>
                                                        <option value="">Selectionner</option>
                                                        <option value="Pourcentage">Pourcentage</option>
                                                        <option value="Nombre">Nombre</option>
                                                        <option value="Taux">Taux</option>
                                                    </select>
                                                </Field>
                                                <Field label="Indicateur">
                                                    <input className="on-input" value={editForm.indicateur} onChange={(event) => updateEditForm({ indicateur: event.target.value })} />
                                                </Field>
                                                <Field label="Performance cible">
                                                    <input className="on-input" type="number" step="0.01" value={editForm.performanceCible} onChange={(event) => updateEditForm({ performanceCible: event.target.value })} />
                                                </Field>
                                                <Field label="Poids (%)" required>
                                                    <input className="on-input" type="number" value={editForm.poids} onChange={(event) => updateEditForm({ poids: event.target.value })} />
                                                </Field>
                                            </div>
                                            <div className="on-form__row on-form__row--2">
                                                <Field label="Surperformance">
                                                    <input className="on-input" type="number" step="0.01" value={editForm.surperformance} onChange={(event) => updateEditForm({ surperformance: event.target.value })} />
                                                </Field>
                                                <Field label="Tolerance">
                                                    <input className="on-input" type="number" step="0.01" value={editForm.tolerance} onChange={(event) => updateEditForm({ tolerance: event.target.value })} />
                                                </Field>
                                            </div>
                                            <div className="on-form__section-label">Type d'objectif</div>
                                            <div className="on-radio-group">
                                                <label className={`on-radio-option ${editForm.typeObjectif === "QUANTITATIF" ? "on-radio-option--active" : ""}`}>
                                                    <input type="radio" name={`typeObjectif-${objectif.id}`} value="QUANTITATIF" checked={editForm.typeObjectif === "QUANTITATIF"} onChange={(event) => updateEditForm({ typeObjectif: event.target.value })} />
                                                    <span className="on-radio-option__dot" />
                                                    <span>Quantitatif</span>
                                                </label>
                                                <label className={`on-radio-option ${editForm.typeObjectif === "QUALITATIF" ? "on-radio-option--active" : ""}`}>
                                                    <input type="radio" name={`typeObjectif-${objectif.id}`} value="QUALITATIF" checked={editForm.typeObjectif === "QUALITATIF"} onChange={(event) => updateEditForm({ typeObjectif: event.target.value })} />
                                                    <span className="on-radio-option__dot" />
                                                    <span>Qualitatif</span>
                                                </label>
                                            </div>
                                            <Field label="Description">
                                                <textarea className="on-textarea" rows={3} value={editForm.description} onChange={(event) => updateEditForm({ description: event.target.value })} />
                                            </Field>
                                            <div className="on-form__footer">
                                                <button className="on-btn on-btn--outline" type="button" onClick={cancelEdit}>Annuler</button>
                                                <button className="on-btn on-btn--primary" type="submit">Enregistrer</button>
                                            </div>
                                        </form>
                                    )}

                                    {objectif.description && <p className="on-item__desc">{objectif.description}</p>}

                                    <div className="on-facts">
                                        <div className="on-fact"><span className="on-fact__key">Type</span><span className="on-fact__val">{objectif.typeObjectif}</span></div>
                                        <div className="on-fact"><span className="on-fact__key">Indicateur</span><span className="on-fact__val">{objectif.indicateur || "-"}</span></div>
                                        <div className="on-fact"><span className="on-fact__key">Type ind.</span><span className="on-fact__val">{objectif.typeIndicateur || "-"}</span></div>
                                        <div className="on-fact"><span className="on-fact__key">Performance cible</span><span className="on-fact__val on-fact__val--accent">{objectif.performanceCible ?? "-"}</span></div>
                                        <div className="on-fact"><span className="on-fact__key">Surperf.</span><span className="on-fact__val">{objectif.surperformance ?? "-"}</span></div>
                                        <div className="on-fact"><span className="on-fact__key">Tolérance</span><span className="on-fact__val">{objectif.tolerance ?? "-"}</span></div>
                                        <div className="on-fact"><span className="on-fact__key">Echéance</span><span className="on-fact__val">{objectif.dateFin ? new Date(objectif.dateFin).toLocaleDateString("fr-FR") : "-"}</span></div>
                                    </div>

                                    {canDecide ? (
                                        <div className="on-item__footer on-manager-decision">
                                            <div className="on-field on-field--inline">
                                                <input
                                                    className="on-input on-input--sm"
                                                    value={comments[objectif.id] || ""}
                                                    onChange={(event) => setComments((current) => ({ ...current, [objectif.id]: event.target.value }))}
                                                    placeholder="Commentaire decision..."
                                                />
                                            </div>
                                            <button className="on-btn on-btn--primary" type="button" onClick={() => dispatch(approveObjectifRequest(objectif.id, payload(objectif)))}>
                                                Valider
                                            </button>
                                            <button className="on-btn on-btn--outline on-btn--danger" type="button" onClick={() => dispatch(rejectObjectifRequest(objectif.id, payload(objectif)))}>
                                                Refuser
                                            </button>
                                        </div>
                                    ) : (
                                        <div >
                                            {/*<span className={`on-status-badge on-status-badge--${String(objectif.approvalStatus || "").toLowerCase()}`}>*/}
                                            {/*    {decisionLabel(objectif.approvalStatus)}*/}
                                            {/*</span>*/}
                                        </div>


                                    )}
                                    {renderRequestHistory(objectif)}
                                </article>
                            );
                        })}
                    </div>
                )}
            </div>
        </StepState>
    );
}
